'use client';
import { gsap } from 'gsap';
import { useRef, useEffect, useState } from 'react';
import dynamic from 'next/dynamic';
import Image from '@repo/ui/src/components/Image/New';
import { buildImageSrc } from '@repo/ui/src/functions/image-functions-aspect-ratio';
import { ToutContentButtons } from '@repo/ui/src/components/ToutContent';
import { ImageProps, MediaType, HeaderVariationProps } from '@repo/ui/src/types';
import { AlignH } from '@repo/ui/src/types/general';
import { AmplienceButtonProps } from '@repo/ui/src/types/button';
import { useMounted } from '@repo/ui/src/hooks/useMounted';
import { useIsMobile, useReducedMotion } from '@repo/ui/src/utils/useMediaQuery';
import { classNames } from '@repo/ui/src/utils/classNames';
import PauseIcon from '@repo/ui/src/components/Icons/PauseIcon';
import PlayIcon from '@repo/ui/src/components/Icons/PlayIcon';
import { appConfig } from '../../utils/appConfig';

const BackgroundVideo = dynamic(() => import('@repo/ui/src/components/BackgroundVideo'), {
  ssr: false,
});

interface HomeHeroProps {
  lilHeading: string;
  headingLine1: string;
  headingLine2: string;
  headingLine3: string;
  subheader?: string;
  headerVariation?: HeaderVariationProps;
  image?: ImageProps;
  buttons: Array<AmplienceButtonProps>;
  align: AlignH;
  backgroundImage: MediaType;
  backgroundMedia?: MediaType;
  _testID?: string;
}

const HomeHero = ({
  lilHeading,
  headingLine1,
  headingLine2,
  headingLine3,
  subheader,
  headerVariation,
  image,
  buttons,
  align,
  backgroundImage,
  backgroundMedia,
}: HomeHeroProps) => {
  const fullSize = !!(headingLine1 && image?.image && buttons?.length);
  const ref = useRef<any>(null);
  const [isVideoPlaying, setIsVideoPlaying] = useState(true);
  const imageUrl = buildImageSrc(image, 500, 0, 0);
  const reduceMotion = useReducedMotion();
  const isMounted = useMounted();
  const xsOnly = useIsMobile();

  const VIDEO_META_SCHEMA = 'http://bigcontent.io/cms/schema/v1/core#/definitions/video-link';

  const activeMedia = backgroundMedia || backgroundImage;
  const isVideo = activeMedia?._meta?.schema === VIDEO_META_SCHEMA;
  const isImage = !isVideo;

  useEffect(() => {
    if (!isMounted) return;

    if (reduceMotion) {
      setIsVideoPlaying(false);
    }

    const element = ref?.current;
    const content = element?.querySelector('.hero-banner-content');
    const fade = element?.querySelector('.fade-container');
    const timeline = gsap.timeline();
    timeline
      .to(fade, {
        delay: 0.2,
        opacity: 0,
        duration: 1,
        ease: 'power3.out',
      })
      .fromTo(
        content,
        { translateY: reduceMotion ? '0%' : '7%' },
        { translateY: '0%', opacity: 1, duration: 1, ease: 'power3.out' },
      );
  }, [isMounted, reduceMotion]);

  const width = xsOnly ? 600 : 2000;
  let aspectRatioWidth: number, aspectRatioHeight: number;
  if (xsOnly && fullSize) {
    aspectRatioWidth = 9;
    aspectRatioHeight = 16;
  } else {
    aspectRatioWidth = 16;
    aspectRatioHeight = 9;
  }

  const backgroundImageUrl =
    isImage && activeMedia
      ? buildImageSrc(activeMedia, width, aspectRatioWidth, aspectRatioHeight)
      : null;

  const videoWidth = width > 1920 ? 1920 : width;

  const togglePlayVideo = () => {
    const element = ref.current;
    if (element) {
      const videoElement = element.querySelector('video');
      if (videoElement) {
        if (isVideoPlaying) {
          videoElement.pause();
          setIsVideoPlaying(false);
        } else {
          videoElement.play().catch(() => {});
          setIsVideoPlaying(true);
        }
      }
    }
  };

  const getAlignment = (align: AlignH) => {
    if (align === 'left') return 'tw-items-start tw-text-left';
    if (align === 'right') return 'tw-items-end tw-text-right';
    return 'tw-items-center tw-text-center';
  };
  const alignment = getAlignment(align);

  const HeadingElement = headerVariation?.headerVariation || 'h2';

  return (
    <section
      ref={ref}
      className={classNames(
        'tw-relative tw-overflow-hidden tw-w-full tw-min-h-[75vh]',
        isImage && 'tw-bg-cover tw-bg-center',
        fullSize && 'tw-h-screen',
      )}
      style={isImage ? { backgroundImage: `url(${backgroundImageUrl})` } : {}}
    >
      {isVideo && activeMedia && (
        <div className="tw-absolute tw-inset-0 tw-z-0">
          <BackgroundVideo
            video={activeMedia}
            width={videoWidth}
            fillContainer
            className="tw-w-full tw-h-full tw-object-cover"
            videoOverlay={false}
          />
        </div>
      )}

      <div className="fade-container tw-absolute tw-inset-0 tw-bg-black tw-opacity-100 tw-z-0 tw-min-h-[75vh]"></div>
      <div
        className={classNames(
          'tw-h-full tw-bg-auto tw-z-10 tw-relative tw-min-h-[75vh]',
          align === 'left' && 'tw-bg-gradient-to-l',
          align === 'right' && 'tw-bg-gradient-to-r',
          align === 'left' || align === 'right'
            ? 'tw-to-[rgba(0,0,0,0.5)_18%] tw-from-transparent'
            : 'tw-bg-[radial-gradient(circle,rgba(0,0,0,0.7)_18%,transparent_100%)]',
        )}
      >
        <div
          className={classNames(
            'hero-banner-content tw-opacity-0 tw-flex tw-flex-col tw-justify-center tw-h-full tw-min-h-[75vh] tw-p-[3vw]',
            appConfig.isMeater && 'tw-py-[5vw]',
            alignment,
          )}
        >
          {(headingLine1 || headingLine2 || headingLine3) && (
            <HeadingElement
              className={classNames(
                'tra-text-web-banner tw-relative tw-text-neutral-100',
                !subheader && 'tw-mb-8 md:tw-mb-12',
                appConfig.isTraeger && 'tra-text-web-banner tw-text-[clamp(5rem,3.5rem+6vw,10rem)]',
              )}
            >
              {lilHeading && (
                <span
                  className={classNames(
                    'tw-block tw-text-[30%] tw-leading-[120%]',
                    appConfig.isMeater && 'tra-text-web-heading-4 tw-uppercase',
                  )}
                >
                  {lilHeading}
                </span>
              )}
              <span className="sr-only">&nbsp;</span>
              {headingLine1 && (
                <span
                  className={classNames(
                    'tw-block tw-mt-2',
                    appConfig.isMeater && 'tra-text-web-heading-1',
                  )}
                >
                  {headingLine1}
                </span>
              )}
              <span className="sr-only">&nbsp;</span>
              {headingLine2 && (
                <span
                  className={classNames(
                    'tw-block',
                    appConfig.isMeater && 'tra-text-web-heading-1 tw-mt-2',
                  )}
                >
                  {headingLine2}
                </span>
              )}
              <span className="sr-only">&nbsp;</span>
              <span
                className={classNames(
                  'tw-font-bold',
                  appConfig.isMeater && 'tra-text-web-heading-1',
                )}
              >
                {headingLine3}
              </span>
            </HeadingElement>
          )}

          {subheader && (
            <div className="tw-my-8 md:tw-my-12 tw-max-w-[38rem]">
              <hr className="tw-border-2 tw-w-10 tw-border-brand-300 tw-mb-8" />
              <p className="tra-text-web-body-xl tw-text-neutral-100">{subheader}</p>
            </div>
          )}

          {image?.image && (
            <div className="tw-mb-8 tw-relative tw-w-[58vw] md:tw-w-[18vw] portrait:md:tw-w-[36vw]">
              <Image
                src={imageUrl}
                alt={image.image.name || "Hero logo"}
                width={500}
                height={500}
                className="tw-w-full tw-h-auto"
                priority
              />
            </div>
          )}

          <ToutContentButtons
            buttons={buttons}
            align={align}
            buttonSize={appConfig.isMeater ? 'medium' : 'large'}
            buttonNegative
            dark
          />
        </div>

        {isVideo && !reduceMotion && (
          <button
            className="tw-absolute tw-left-10 tw-bottom-3 tw-z-[1010] md:tw-left-5 hover:tw-text-brand-400"
            aria-label={isVideoPlaying ? 'Pause Video' : 'Play Video'}
            onClick={togglePlayVideo}
          >
            {isVideoPlaying ? <PauseIcon color="white" /> : <PlayIcon color="white" />}
          </button>
        )}
      </div>
    </section>
  );
};

export default HomeHero;
